import { createSlice } from '@reduxjs/toolkit';
import { t } from 'i18next';
import { showSuccess } from '../../app/utils/sweetAlert';
import { showSuccessToast } from '../../app/utils/tostify';
import { getSearchData } from '../apis/searchAPIs';
import { getAssignTenantsList, getUsersList } from '../apis/systemUserAPI';
import {
	GetDefaultTenant,
	GetTenantDetails,
	getTenantList,
	CreateTenantDetails,
	UpdateTenantDetails,
	getSearchTenantList,
	linkedTenants,
	deleteTenant,
	createBranched,
	allowNewItems,
	allowNewProducts,
	updateBranched,
	GetTenantServices
} from '../apis/tenantAPIs';

interface TenantState {
	loading: boolean;
	searchLoading: boolean;
	error: any;
	//default
	advanceLimit: Array<any>;
	notificationTypes: Array<any>;
	tenantServices: Array<any>;
	//get tenant id
	tenantDetails: Object;
	tenantServicePermissions: Array<any>;
	tenantBranches: Array<any>;
	details: Object;
	notification: object;
	tenantList: Array<any>;
	count: number;
	lastBranchId: number;
	searchTenantList: Array<any>;
	linkedTenantList: Array<any>;
	currentPage: number;
	successAlert: any;
	holdIntegration: any;
	sapIntegration: any;
	searchKey: string;
	// primary_contact_id: Object;
	// city: object;
	// country: object;
	// advance_limit_value: Array<any>;
}

const initialState: TenantState = {
	advanceLimit: [],
	loading: false,
	searchLoading: false,
	error: '',
	successAlert: '',
	holdIntegration: null,
	sapIntegration: null,
	notificationTypes: [],
	tenantServices: [],
	tenantDetails: {},
	tenantServicePermissions: [],
	details: {},
	notification: {},
	tenantList: [],
	tenantBranches: [],
	count: 0,
	lastBranchId: 1,
	searchTenantList: [],
	linkedTenantList: [],
	currentPage: 1,
	searchKey: ''
};

const TenantSlice = createSlice({
	name: 'TenantSlice',
	initialState,
	reducers: {
		detailsOfTenant(state, action: any) {
			state.details = action.payload;
		},
		updateLoading(state, action: any) {
			state.loading = action.payload;
		},
		setHoldIntegration(state, action: any) {
			state.holdIntegration = action.payload;
		},
		setSapIntegration(state, action: any) {
			state.sapIntegration = action.payload;
		},
		notificationDetailsOftenant(state, action: any) {
			state.notification = action.payload;
		},
		addBranchesIntenantDetails(state, action: any) {
			state.tenantBranches = action.payload;
		},
		linkTenatnWIthTenant(state, action: any) {
			state.linkedTenantList.push(action.payload);
			state.searchTenantList = state.searchTenantList.map((item: any) => {
				const answer = item._id == action.payload._id;
				return answer ? { ...item, isAdded: true } : item;
			});
		},
		removeLinkedTenantInSlice(state, action: any) {
			state.linkedTenantList = action.payload;
		},
		incrementCount(state) {
			state.lastBranchId += 1;
		},
		decrementLastBranchId(state) {
			state.lastBranchId -= 1;
		},
		setLastBranchId(state) {
			state.lastBranchId = 1;
		},
		handlePagination(state, action: any) {
			state.currentPage = action.payload;
		},
		handleSearchKey(state, action: any) {
			state.searchKey = action.payload;
		}
	},
	extraReducers: builder => {
		//for default tenant
		builder.addCase(GetDefaultTenant.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
		});
		builder.addCase(GetDefaultTenant.fulfilled, (state: TenantState, action: any) => {
			state.loading = false;
			state.error = '';
			state.advanceLimit = action?.payload?.advanceLimit;
			state.notificationTypes = action?.payload?.notificationTypes;
			state.tenantServices = action?.payload?.tenantServices;
			state.linkedTenantList = [];
		});
		builder.addCase(GetDefaultTenant.rejected, (state: TenantState) => {
			state.loading = false;
		});

		//for tenant details using ID

		builder.addCase(GetTenantDetails.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
			state.tenantDetails = {};
			state.tenantBranches = [];
			state.linkedTenantList = [];
		});
		builder.addCase(GetTenantDetails.fulfilled, (state: TenantState, action: any) => {
			state.loading = false;
			state.error = '';
			state.tenantDetails = action.payload;
			state.tenantBranches = action.payload.tenantBranches;
			state.lastBranchId = action.payload.tenantDetails.last_branch_id;
			state.linkedTenantList = action.payload.tenantDetails.linked_tenant_id;
		});
		builder.addCase(GetTenantDetails.rejected, (state: TenantState) => {
			state.loading = false;
		});
		//for remove LinkedTenantLIst
		builder.addCase(getUsersList.pending, state => {
			state.loading = true;
			state.linkedTenantList = [];
		});
		builder.addCase(getUsersList.fulfilled, state => {
			state.loading = false;
		});
		builder.addCase(getUsersList.rejected, state => {
			state.loading = false;
		});
		builder.addCase(getAssignTenantsList.pending, state => {
			state.loading = true;
		});
		builder.addCase(getAssignTenantsList.fulfilled, (state, action: any) => {
			state.loading = false;
			state.linkedTenantList = action.payload.list;
		});
		builder.addCase(getAssignTenantsList.rejected, state => {
			state.loading = false;
		});

		//for tenant list

		builder.addCase(getTenantList.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
			state.successAlert = '';
		});
		builder.addCase(getTenantList.fulfilled, (state: TenantState, action: any) => {
			state.loading = false;
			state.error = '';
			state.successAlert = '';
			state.tenantList = action.payload.list;
			state.count = action.payload.count;
		});
		builder.addCase(getTenantList.rejected, (state: TenantState) => {
			state.loading = false;
		});
		builder.addCase(CreateTenantDetails.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
			state.successAlert = '';
		});
		builder.addCase(CreateTenantDetails.fulfilled, (state: TenantState) => {
			state.loading = false;
			state.error = '';
			// state.successAlert = [{ message: "Tenant has been successfully created." }]
			showSuccess({ message: 'Tenant has been successfully created.' });
		});
		builder.addCase(
			CreateTenantDetails.rejected,
			(state: TenantState, action: any) => {
				state.loading = false;
				state.error = [{ message: t(action.payload?.data?.message) }];
				state.successAlert = '';
			}
		);
		builder.addCase(UpdateTenantDetails.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
			state.successAlert = '';
		});
		builder.addCase(UpdateTenantDetails.fulfilled, (state: TenantState) => {
			state.loading = false;
			state.error = '';
			// state.successAlert = [{ message: "your changes have been saved." }]
			showSuccess({ message: 'Your changes have been saved.' });
		});
		builder.addCase(
			UpdateTenantDetails.rejected,
			(state: TenantState, action: any) => {
				state.loading = false;
				state.error = [{ message: t(action.payload.data.message) }];
				state.successAlert = '';
			}
		);
		builder.addCase(getSearchTenantList.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
		});
		builder.addCase(
			getSearchTenantList.fulfilled,
			(state: TenantState, action: any) => {
				state.loading = false;
				state.error = '';
				state.searchTenantList = action.payload.map((item: any) => {
					const answer = state.linkedTenantList.some(
						(Linkedtenant: any) => Linkedtenant._id == item._id
					);
					return { ...item, isAdded: answer };
				});
			}
		);
		builder.addCase(
			getSearchTenantList.rejected,
			(state: TenantState, action: any) => {
				state.loading = false;
				state.error = [{ message: t(action.payload.data.message) }];
			}
		);
		builder.addCase(linkedTenants.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
		});
		builder.addCase(linkedTenants.fulfilled, (state: TenantState) => {
			state.loading = false;
			state.error = '';
		});
		builder.addCase(linkedTenants.rejected, (state: TenantState) => {
			state.loading = false;
			// state.error = [{ message: action.payload.data.message }]
		});
		builder.addCase(deleteTenant.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
			state.successAlert = '';
		});
		builder.addCase(deleteTenant.fulfilled, (state: TenantState) => {
			state.loading = false;
			state.error = '';
			// state.successAlert = [{ message: "Tenant has been successfully deleted." }]
			showSuccessToast({
				message: 'Tenant has been successfully deleted.'
			});
		});
		builder.addCase(deleteTenant.rejected, (state: TenantState) => {
			state.loading = false;
			state.successAlert = '';
			// state.error = [{ message: action.payload.data.message }]
		});
		builder.addCase(createBranched.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
			state.successAlert = '';
		});
		builder.addCase(createBranched.fulfilled, (state: TenantState) => {
			state.loading = false;
			state.error = '';
			// state.successAlert = [{ message: "Branch has been successfully created." }]
			showSuccess({ message: 'Branch has been successfully created.' });
		});
		builder.addCase(createBranched.rejected, (state: TenantState) => {
			state.loading = false;
			state.successAlert = '';
			// state.error = [{ message: action.payload.data.message }]
		});
		builder.addCase(updateBranched.fulfilled, (state: TenantState) => {
			state.loading = false;
			state.error = '';
			showSuccess({ message: 'Branch has been updated successfully' });
		});
		builder.addCase(getSearchData.pending, (state: TenantState) => {
			state.searchLoading = true;
			state.error = '';
		});
		builder.addCase(getSearchData.fulfilled, (state: TenantState) => {
			state.searchLoading = false;
			state.error = '';
		});
		builder.addCase(getSearchData.rejected, (state: TenantState) => {
			state.searchLoading = false;
			state.successAlert = '';
		});
		builder.addCase(allowNewItems.pending, (state: TenantState) => {
			// state.loading = true;
			state.error = '';
		});
		builder.addCase(allowNewItems.fulfilled, (state: TenantState) => {
			state.loading = false;
			state.error = '';
		});
		builder.addCase(allowNewItems.rejected, (state: TenantState) => {
			state.loading = false;
		});
		builder.addCase(allowNewProducts.pending, (state: TenantState) => {
			// state.loading = true;
			state.error = '';
		});
		builder.addCase(allowNewProducts.fulfilled, (state: TenantState) => {
			state.loading = false;
			state.error = '';
		});
		builder.addCase(allowNewProducts.rejected, (state: TenantState) => {
			state.loading = false;
		});
		builder.addCase(GetTenantServices.pending, (state: TenantState) => {
			state.loading = true;
			state.error = '';
			state.tenantServicePermissions = [];
		});
		builder.addCase(
			GetTenantServices.fulfilled,
			(state: TenantState, action: any) => {
				state.loading = false;
				state.error = '';
				state.tenantServicePermissions = action?.payload?.services;
			}
		);
		builder.addCase(GetTenantServices.rejected, (state: TenantState) => {
			state.loading = false;
		});
	}
});
export const {
	detailsOfTenant,
	notificationDetailsOftenant,
	addBranchesIntenantDetails,
	linkTenatnWIthTenant,
	incrementCount,
	updateLoading,
	setSapIntegration,
	setHoldIntegration,
	removeLinkedTenantInSlice,
	setLastBranchId,
	decrementLastBranchId,
	handlePagination,
	handleSearchKey
} = TenantSlice.actions;
export default TenantSlice.reducer;
